import React, { useEffect, useState } from "react";
import {useNavigate} from "react-router-dom";
import { 
    canAccessAnalytics, 
    canAccessIntegrations, 
    canAccessSettings, 
    canAccessMultiUserTours,
    isMultiUser,
    getRoleName,
    isUser
} from "../utils/roleUtils";

const uniAdminSystemItems = [
    { path: "/users", icon: "fas fa-users", label: "Users" },
    { path: "/university-integrations", icon: "fas fa-random", label: "Integration Management" },
    { path: "/crm-source-integrations", icon: "fas fa-cloud-download-alt", label: "CRM Source Integrations" },
];

const superAdminSystemItems = [
    { path: "/universities", icon: "fas fa-school", label: "Universities" },
    { path: "/users", icon: "fas fa-users", label: "Users" },
    { path: "/integration-templates", icon: "fas fa-cube", label: "Integration Templates" },
    { path: "/university-integrations", icon: "fas fa-random", label: "Integration Management" },
    { path: "/crm-source-integrations", icon: "fas fa-cloud-download-alt", label: "CRM Source Integrations" },
    { path: "/manage-analytics", icon: "fas fa-chart-area", label: "Analytics" },
    { path: "/settings", icon: "fas fa-cog", label: "Settings" },
];

const multiUserTourOperationsItems = [
    { path: "/multi-tours", icon: "fas fa-map", label: "Tours" },
    { path: "/multi-events", icon: "fas fa-calendar", label: "Events" },
    { path: "/multi-form-templates", icon: "fas fa-file-alt", label: "Forms" },
    { path: "/multi-users", icon: "fas fa-users", label: "Users" },
    { path: "/multi-templates", icon: "fas fa-inbox", label: "Email Templates" },
];

const Sidebar = () => {
    const [activePath, setActivePath] = useState(window.location.pathname);
    const navigate = useNavigate();
    const firstName = localStorage.getItem("firstName");
    const userRole = localStorage.getItem("role");

    const handleLogout = (event) => {
        event.preventDefault();
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("universityId");
        navigate("/login");
    };

    useEffect(() => {
        const handlePathChange = () => setActivePath(window.location.pathname);
        window.addEventListener("popstate", handlePathChange);
        return () => window.removeEventListener("popstate", handlePathChange);
    }, []);

    // Main section items
    let mainItems = [
        { path: "/dashboard", icon: "fas fa-home", label: "Dashboard" },
    ];
    
    // Add tours, forms, and templates for non-super-admin users
    if (userRole !== "super_admin") {
        mainItems.push(
            { path: "/tours", icon: "fas fa-map", label: "Tours" },
            { path: "/forms", icon: "fas fa-file-alt", label: "Forms" },
            { path: "/templates", icon: "fas fa-inbox", label: "Email Templates" }
        );
    }

    // Analytics section items
    let analyticsItems = [];
    if (userRole !== "super_admin" && canAccessAnalytics()) {
        analyticsItems = [
            { path: "/analytics", icon: "fas fa-chart-bar", label: "Analytics" },
        ];
    }

    // System section items
    let systemItems = [];
    if (userRole === "super_admin") {
        systemItems = superAdminSystemItems;
    } else if (userRole === "uni_admin") {
        systemItems = uniAdminSystemItems;
    }

    // Tour Operations section for multi-users
    let tourOperationsItems = [];
    if (isMultiUser()) {
        tourOperationsItems = multiUserTourOperationsItems;
    }

    return (
        <aside className="sidebar">
            <div className="logo">
                <img height="42" alt="Kai Logo" src="/libraries/assets/images/glc_logo.png" />
                <span className="logo-text"></span>
            </div>
            <nav className="nav">
                {/* MAIN SECTION */}
                <div className="nav-section">
                    <h6 className="nav-section-title">MAIN</h6>
                    <ul>
                        {mainItems.map(item => (
                            <li key={item.path}>
                                <a className={activePath === item.path ? "active" : ""} href={item.path}>
                                    <i className={item.icon}></i>
                                    <span>{item.label}</span>
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
                {/* ANALYTICS SECTION */}
                {analyticsItems.length > 0 && (
                    <div className="nav-section">
                        <h6 className="nav-section-title">ANALYTICS</h6>
                        <ul>
                            {analyticsItems.map(item => (
                                <li key={item.path}>
                                    <a className={activePath === item.path ? "active" : ""} href={item.path}>
                                        <i className={item.icon}></i>
                                        <span>{item.label}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {/* TOUR OPERATIONS SECTION */}
                {tourOperationsItems.length > 0 && (
                    <div className="nav-section">
                        <h6 className="nav-section-title">TOUR OPERATIONS</h6>
                        <ul>
                            {tourOperationsItems.map(item => (
                                <li key={item.path}>
                                    <a className={activePath === item.path ? "active" : ""} href={item.path}>
                                        <i className={item.icon}></i>
                                        <span>{item.label}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {/* SYSTEM SECTION */}
                {systemItems.length > 0 && (
                    <div className="nav-section">
                        <h6 className="nav-section-title">SYSTEM</h6>
                        <ul>
                            {systemItems.map(item => (
                                <li key={item.path}>
                                    <a className={activePath === item.path ? "active" : ""} href={item.path}>
                                        <i className={item.icon}></i>
                                        <span>{item.label}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </nav>
            <div className="sidebar-footer">
                <div className="profile">
                    <div className="profile-info">
                        <div className="profile-icon">{firstName ? firstName.charAt(0) : "U"}</div>
                        <div className="profile-details">
                            <span className="profile-name">{firstName}</span>
                            <span className="profile-role">{getRoleName() || userRole}</span>
                        </div>
                    </div>
                    <button className="logout-btn" onClick={handleLogout}>
                        <i className="fas fa-sign-out-alt"></i>
                        <span>Logout</span>
                    </button>
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;